import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { formatCurrency } from '../../../utils/formatters';
import { API_DOCS } from './LiquidazioniIvaPage.api';
import './Scritture.css';

const MODULO = 'amministrazione.liquidazioni_iva';

type TipoPeriodo = 'MENSILE' | 'TRIMESTRALE';
type StatoLiq = 'APERTA' | 'DEFINITIVA';

interface Liquidazione {
  id: number;
  societaId: number;
  anno: number;
  periodo: number;
  tipoPeriodo: TipoPeriodo;
  label: string;
  stato: StatoLiq;
  dataDefinizione: string | null;
  ivaDaVersare: number | null;
  creditoDaRiportare: number | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

const MESI_LABEL = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const TRIMESTRI_LABEL = ['Q1 (Gen-Mar)', 'Q2 (Apr-Giu)', 'Q3 (Lug-Set)', 'Q4 (Ott-Dic)'];

export default function LiquidazioniIvaPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const navigate = useNavigate();

  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [periodicita, setPeriodicita] = useState<TipoPeriodo>('MENSILE');
  const [items, setItems] = useState<Liquidazione[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anno, setAnno] = useState<number>(new Date().getFullYear());

  const load = async (silent = false) => {
    if (!societaId) { setItems([]); setLoading(false); return; }
    if (!silent) setLoading(true);
    setError('');
    try {
      // @api GET /api/liquidazioni-iva?societaId=&anno= -> { success: true, data: [{ id, anno, periodo, tipoPeriodo, label, stato, dataDefinizione, note }], periodicita: 'MENSILE'|'TRIMESTRALE' }
      const res = await apiFetch<{ success: boolean; data: Liquidazione[]; periodicita: TipoPeriodo }>(
        `/api/liquidazioni-iva?societaId=${societaId}&anno=${anno}`,
      );
      setItems(res.data ?? []);
      setPeriodicita(res.periodicita || 'MENSILE');
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, [societaId, anno]);

  // Mappa periodo → liquidazione
  const mappa = useMemo(() => {
    const m = new Map<number, Liquidazione>();
    for (const l of items) {
      if (l.tipoPeriodo === periodicita) m.set(l.periodo, l);
    }
    return m;
  }, [items, periodicita]);

  const labels = periodicita === 'TRIMESTRALE' ? TRIMESTRI_LABEL : MESI_LABEL;
  const totalePeriodi = periodicita === 'TRIMESTRALE' ? 4 : 12;

  const creaLiquidazione = async (periodo: number) => {
    if (!societaId) return;
    try {
      // @api POST /api/liquidazioni-iva { societaId, anno, periodo, tipoPeriodo } -> { success: true, data: { id, stato } }
      await apiFetch('/api/liquidazioni-iva', {
        method: 'POST',
        body: JSON.stringify({ societaId: Number(societaId), anno, periodo, tipoPeriodo: periodicita }),
      });
      load(true);
    } catch (e: any) {
      window.alert(e.message || 'Errore');
    }
  };

  const noSocieta = !societaId;
  const annoCorrente = new Date().getFullYear();

  return (
    <Layout>
      <div className="scritture-contabili liquidazioni-iva">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Liquidazioni IVA</h2>
            <p>
              Apertura e chiusura periodi IVA — periodicità{' '}
              <strong>{periodicita === 'TRIMESTRALE' ? 'trimestrale' : 'mensile'}</strong>{' '}
              (configurabile su Società). Marcare un periodo DEFINITIVO congela le scritture IVA con data in quel periodo.
            </p>
          </div>
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          <label className="bop-flex liq-anno-label">
            <span>Anno:</span>
            <select className="bop-select liq-anno-select" value={anno} onChange={e => setAnno(Number(e.target.value))}>
              {[-2, -1, 0, 1].map(off => {
                const y = annoCorrente + off;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
          </label>
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        <div className="bop-card">
          {noSocieta ? (
            <div className="bop-empty-state">Seleziona una società per vedere le liquidazioni IVA.</div>
          ) : loading ? (
            <div className="bop-empty-state">Caricamento…</div>
          ) : (
            <div className="liq-griglia">
              {Array.from({ length: totalePeriodi }, (_, i) => i + 1).map(periodo => {
                const liq = mappa.get(periodo);
                const stato = liq?.stato || 'NUOVA';
                const clickable = !!liq;
                return (
                  <div
                    key={periodo}
                    className={`liq-cella liq-cella--${stato.toLowerCase()} ${clickable ? 'liq-cella--clickable' : ''}`}
                    onClick={clickable ? () => navigate(`/amministrazione/liquidazioni-iva/${liq!.id}`) : undefined}
                  >
                    <div className="liq-cella__header">
                      <span className="liq-cella__periodo">{labels[periodo - 1]}</span>
                      <span className={`bop-badge ${
                        stato === 'DEFINITIVA' ? 'bop-badge--success' :
                        stato === 'APERTA' ? 'bop-badge--warning' :
                        'bop-badge--neutral'
                      }`}>
                        {stato === 'DEFINITIVA' ? 'Definitiva' : stato === 'APERTA' ? 'Aperta' : 'Non creata'}
                      </span>
                    </div>

                    <div className="liq-cella__body">
                      {liq?.dataDefinizione && (
                        <div className="liq-cella__data">
                          Chiusa il: <strong>{new Date(liq.dataDefinizione).toLocaleDateString('it-IT')}</strong>
                        </div>
                      )}
                      {liq?.stato === 'DEFINITIVA' && liq.ivaDaVersare != null && liq.ivaDaVersare > 0 && (
                        <div className="liq-cella__saldo liq-cella__saldo--debito">
                          Da versare: {formatCurrency(liq.ivaDaVersare)}
                        </div>
                      )}
                      {liq?.stato === 'DEFINITIVA' && liq.creditoDaRiportare != null && liq.creditoDaRiportare > 0 && (
                        <div className="liq-cella__saldo liq-cella__saldo--credito">
                          Credito: {formatCurrency(liq.creditoDaRiportare)}
                        </div>
                      )}
                      {liq?.note && <div className="liq-cella__note">{liq.note}</div>}
                    </div>

                    <div className="liq-cella__actions" onClick={e => e.stopPropagation()}>
                      {!liq && canEdit && (
                        <button className="bop-btn bop-btn-secondary bop-btn--sm" onClick={() => creaLiquidazione(periodo)}>
                          Apri periodo
                        </button>
                      )}
                      {liq && (
                        <button className="bop-btn bop-btn-secondary bop-btn--sm" onClick={() => navigate(`/amministrazione/liquidazioni-iva/${liq.id}`)}>
                          Apri dettaglio
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
